import React from 'react';
import { t } from '@citrite/translate';
import {
	plainConfirmModal,
	successConfirmModal,
	warningConfirmModal,
} from '@citrite/web-ui-component';
import { first } from 'lodash';
import {
	RadioSubActionsContent,
	SubActionOption,
} from 'App/Activity/components/RadioSubActionsContent';
import {
	DesktopAction,
	LaunchAction,
	MachineAction,
	Session,
	SessionAction,
	SessionType,
} from 'App/Activity/ResourceManagerContext';

const localeActionNameMap = {
	[SessionAction.LOGOUT]: 'logout',
	[SessionAction.DISCONNECT]: 'disconnect',
	[MachineAction.SHUTDOWN]: 'shutdown',
	[MachineAction.HIBERNATE]: 'hibernate',
	[MachineAction.POWEROFF]: 'poweroff',
	[LaunchAction.TRANSFER]: 'transfer',
	[MachineAction.CONTROLLED_RESTART]: 'restart',
};

const getControlledRestartSubActionOptions = (): SubActionOption[] => [
	{
		action: MachineAction.POWEROFF,
		label: t('Workspace:restart_desktop_modal.force_restart_label'),
		description: t('Workspace:restart_desktop_modal.force_restart_description'),
	},
	{
		action: MachineAction.SHUTDOWN,
		label: t('Workspace:restart_desktop_modal.graceful_restart_label'),
		description: t('Workspace:restart_desktop_modal.graceful_restart_description'),
	},
];

const localeActionStringsMap = {
	[SessionAction.LOGOUT]: 'Workspace:log_out_desktop_modal',
	[SessionAction.DISCONNECT]: 'Workspace:disconnect_desktop_modal',
	[MachineAction.SHUTDOWN]: 'Workspace:shutdown_desktop_modal',
	[MachineAction.HIBERNATE]: 'Workspace:hibernate_desktop_modal',
	[MachineAction.POWEROFF]: 'Workspace:poweroff_desktop_modal',
	[LaunchAction.TRANSFER]: 'Workspace:session_handoff_desktop_modal',
	[MachineAction.CONTROLLED_RESTART]: 'Workspace:restart_desktop_modal',
};

const getLocaleString = (
	action: DesktopAction,
	session: Session,
	type: 'title' | 'description'
) => {
	const desktopName = session.applications[0]?.name;
	const key = `${localeActionStringsMap[action]}.${type}`;
	const sessionName =
		session.userSessionType === SessionType.DESKTOP
			? { desktopName }
			: { desktopName: session?.machineData?.machineName };
	return t(key, sessionName);
};

const getLocaleText = (action: DesktopAction, session: Session) => {
	const confirmButtonText = t(
		`${localeActionStringsMap[action]}.${localeActionNameMap[action]}`
	);
	const description = getLocaleString(action, session, 'description');
	const note = t(`${localeActionStringsMap[action]}.note`);
	const title = getLocaleString(action, session, 'title');
	return { confirmButtonText, description, note, title };
};

interface ExtraContentConfig<T = unknown> {
	Component: React.ComponentType<T>;
	props?: T;
}

type SubActionsContentProps = React.ComponentProps<typeof RadioSubActionsContent>;

export const getConfirmationDetails = (action: DesktopAction, session: Session) => {
	const localeTexts: { [key: string]: string } = {};
	let confirmModal = warningConfirmModal;
	let extraContent: ExtraContentConfig<SubActionsContentProps> | null = null;

	switch (action) {
		case SessionAction.LOGOUT:
			break;
		case MachineAction.POWEROFF:
		case MachineAction.SHUTDOWN:
			localeTexts.note = '';
			break;
		case SessionAction.DISCONNECT:
			confirmModal = plainConfirmModal;
			break;
		case MachineAction.HIBERNATE:
			localeTexts.description = getLocaleString(action, session, 'description');
			confirmModal = successConfirmModal;
			break;
		case LaunchAction.TRANSFER:
			localeTexts.title = t('Workspace:session_handoff_desktop_modal.title');
			localeTexts.note = '';
			localeTexts.description = getLocaleString(action, session, 'description');
			break;
		case MachineAction.CONTROLLED_RESTART:
			localeTexts.title = t('Workspace:restart_desktop_modal.title', {
				desktopName: first(session?.applications)?.name,
			});
			localeTexts.description = t(
				'Workspace:restart_desktop_modal.choose_how_to_restart'
			);
			localeTexts.note = '';
			extraContent = {
				Component: RadioSubActionsContent,
				props: {
					subActionOptions: getControlledRestartSubActionOptions(),
					onChange: () => {},
				},
			};
			break;
		default:
			return undefined;
	}
	return {
		confirmModal,
		extraContent,
		...getLocaleText(action, session),
		...localeTexts,
	};
};
